import { useState } from 'react'
import { motion } from 'framer-motion'
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { companyInfo, contactFormFields, seoMeta } from '../data/pages'
import SEO from '../components/SEO'

export default function ContactPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    const form = e.currentTarget
    const body = new URLSearchParams()
    new FormData(form).forEach((value, key) => body.append(key, value.toString()))

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })
      if (response.ok) {
        setIsSubmitted(true)
      } else {
        setError('Something went wrong. Please call us directly or try again.')
      }
    } catch {
      setError('Something went wrong. Please call us directly or try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <SEO title={seoMeta.contact.title} description={seoMeta.contact.description} />

      <section className="relative bg-earth-900 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blossom-950 via-earth-900 to-prime-950" />
        <div className="container-wide section-padding relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge-green mb-4">Contact</span>
            <h1 className="text-display-1 font-bold text-white mb-4">Get in Touch</h1>
            <p className="text-earth-300 text-lg max-w-2xl">Ready for a cleaner future? Get your free, no-obligation quote today. Same-day response guaranteed.</p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding-y section-padding bg-earth-50">
        <div className="container-wide">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-earth-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blossom-100 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-blossom-600" /></div>
                    <div>
                      <div className="font-medium text-earth-900">Address</div>
                      <span className="text-earth-400 text-sm">{companyInfo.address.street}<br />{companyInfo.address.city}, {companyInfo.address.postcode}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blossom-100 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-blossom-600" /></div>
                    <div>
                      <div className="font-medium text-earth-900">Phone</div>
                      <a href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`} className="text-blossom-600 hover:underline text-sm">{companyInfo.contact.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blossom-100 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-blossom-600" /></div>
                    <div>
                      <div className="font-medium text-earth-900">Email</div>
                      <a href={`mailto:${companyInfo.contact.email}`} className="text-blossom-600 hover:underline text-sm">{companyInfo.contact.email}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blossom-100 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-blossom-600" /></div>
                    <div>
                      <div className="font-medium text-earth-900">Hours</div>
                      <p className="text-earth-500 text-sm">{companyInfo.hours}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="aspect-video rounded-2xl overflow-hidden border border-earth-200 shadow-sm">
                <iframe
                  title="Blossom Prime Facility Solutions location"
                  src="https://maps.google.com/maps?q=Pentagon+Centre+Chatham+Kent+ME4+4HW+UK&output=embed&z=15"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-earth-100">
                {isSubmitted ? (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-blossom-100 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-blossom-600" /></div>
                    <h3 className="text-2xl font-bold text-earth-900 mb-2">Message Sent!</h3>
                    <p className="text-earth-500">Thank you for contacting us. We'll respond within 24 hours.</p>
                  </motion.div>
                ) : (
                  <form
                    name="contact"
                    method="post"
                    data-netlify="true"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <h3 className="text-xl font-bold text-earth-900 mb-6">Request a Quote</h3>

                    {error && (
                      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        {error}
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      {contactFormFields.map((field) => (
                        <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                          <label htmlFor={field.name} className="block text-sm font-medium text-earth-700 mb-2">
                            {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.type === 'select' ? (
                            <select
                              id={field.name}
                              name={field.name}
                              required={field.required}
                              className="w-full px-4 py-3 rounded-xl border border-earth-200 focus:border-blossom-500 focus:ring-2 focus:ring-blossom-500/20 outline-none transition-all"
                            >
                              <option value="">Select a service...</option>
                              {'options' in field && field.options?.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : field.type === 'textarea' ? (
                            <textarea
                              id={field.name}
                              name={field.name}
                              required={field.required}
                              placeholder={'placeholder' in field ? field.placeholder : undefined}
                              rows={4}
                              className="w-full px-4 py-3 rounded-xl border border-earth-200 focus:border-blossom-500 focus:ring-2 focus:ring-blossom-500/20 outline-none transition-all resize-none"
                            />
                          ) : (
                            <input
                              type={field.type}
                              id={field.name}
                              name={field.name}
                              required={field.required}
                              placeholder={'placeholder' in field ? field.placeholder : undefined}
                              className="w-full px-4 py-3 rounded-xl border border-earth-200 focus:border-blossom-500 focus:ring-2 focus:ring-blossom-500/20 outline-none transition-all"
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                    >
                      <Send className="w-5 h-5" />
                      {isLoading ? 'Sending…' : 'Send Message'}
                    </button>
                    <p className="text-xs text-earth-400 text-center">Your data is protected under GDPR. We never share your information with third parties.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
